import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Activity, CheckCircle2, Clock, Globe, XCircle } from "lucide-react";
import {
  getDomainCheckAdminStats,
  type DomainCheckAdminStats,
} from "@/lib/domain-check-admin-stats.functions";

export const Route = createFileRoute("/_authenticated/domain-check-stats")({
  component: DomainCheckStatsPage,
  head: () => ({ meta: [{ title: "Domain-Check Statistik — InboxPulse" }] }),
});

function fmtMs(v: number | null) {
  if (v === null) return "—";
  if (v < 1000) return `${v} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

function fmtPct(v: number) {
  return `${(v * 100).toFixed(1)}%`;
}

function DomainCheckStatsPage() {
  const fetchStats = useServerFn(getDomainCheckAdminStats);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["domain-check-admin-stats"],
    queryFn: () => fetchStats(),
    refetchInterval: 30_000,
  });

  const isAdminError = error instanceof Error && /forbidden|admin/i.test(error.message);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Domain-Check Statistik"
        description="Erfolgsquote, Dauer, Abbruchgründe und Top fehlschlagende Provider der letzten 24 Stunden."
        actions={
          <button
            onClick={() => refetch()}
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-accent transition disabled:opacity-50"
            disabled={isFetching}
          >
            {isFetching ? "Aktualisiere…" : "Aktualisieren"}
          </button>
        }
      />

      <HelpPanel
        title="Hilfe: Wie zuverlässig sind die Domain-Checks?"
        what={
          <>
            Statistik der letzten 24 Stunden: <b>Wie viele Checks waren erfolgreich</b>, wie lange
            dauerten sie, und wo gab es Probleme?
          </>
        }
        why={
          <>
            Wenn Checks ständig fehlschlagen, sind deine Domain-Ampeln im Dashboard möglicherweise{" "}
            <b>nicht aktuell</b>. Du würdest ein Problem zu spät bemerken.
          </>
        }
        how={[
          <>Zahlen oben zeigen Erfolgsquote, Durchschnitts-Dauer und Abbruchgrund-Liste.</>,
          <>Provider-Tabelle: welcher Anbieter blockt am meisten.</>,
        ]}
        tips={[
          <>Erfolgsquote unter 90% → Admin informieren, vielleicht Rate-Limit beim Provider.</>,
        ]}
        glossary={[
          {
            term: "Abbruchgrund",
            def: "Warum ein Check nicht fertig wurde (Timeout, Netzwerk-Fehler, Rate-Limit…).",
          },
          {
            term: "Rate-Limit",
            def: "Begrenzung der Anfragen pro Zeit. Zuviele → Anbieter blockt vorübergehend.",
          },
        ]}
      />

      {isLoading && <Card className="p-6 text-muted-foreground">Lade Statistiken…</Card>}

      {error && (
        <Card className="p-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin-Zugriff erforderlich" : "Fehler beim Laden"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {error instanceof Error ? error.message : String(error)}
            </div>
          </div>
        </Card>
      )}

      {!isLoading && !error && data && <Stats data={data} />}
    </div>
  );
}

function Stats({ data }: { data: DomainCheckAdminStats }) {
  const { totals, duration, failureReasons, topFailingProviders } = data;
  const decided = totals.done + totals.failed;
  return (
    <>
      {totals.total === 0 && (
        <Card className="p-4 mb-4 text-sm text-muted-foreground">
          Keine Domain-Checks in den letzten {data.windowHours}h. Sobald Jobs laufen, erscheinen
          hier echte Kennzahlen.
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat
          icon={CheckCircle2}
          label="Erfolgsquote"
          value={decided > 0 ? fmtPct(totals.successRate) : "—"}
          sub={`${totals.done} ok / ${totals.failed} fail`}
          tone={decided > 0 ? "success" : undefined}
        />
        <Stat
          icon={Clock}
          label="Ø Dauer"
          value={fmtMs(duration.avgMs)}
          sub={`p95 ${fmtMs(duration.p95Ms)} · n=${duration.count}`}
        />
        <Stat
          icon={Activity}
          label="In Bearbeitung"
          value={String(totals.pending + totals.running)}
          sub={`${totals.pending} pending · ${totals.running} running`}
        />
        <Stat
          icon={XCircle}
          label="Fehlgeschlagen"
          value={String(totals.failed)}
          sub={`${totals.canceled} abgebrochen · ${totals.total} gesamt`}
          tone={totals.failed > 0 ? "danger" : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <XCircle className="size-4 text-destructive" /> Top Abbruchgründe
          </h3>
          {failureReasons.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Fehler im Zeitraum.</div>
          ) : (
            <BarList items={failureReasons.map((r) => ({ label: r.reason, value: r.count }))} />
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="size-4 text-muted-foreground" /> Top fehlschlagende Provider (TLD)
          </h3>
          {topFailingProviders.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine fehlschlagenden Provider.</div>
          ) : (
            <BarList
              items={topFailingProviders.map((p) => ({
                label: p.provider,
                value: p.failed,
                sub: `${p.failed}/${p.total} · ${fmtPct(p.failRate)}`,
              }))}
            />
          )}
        </Card>
      </div>

      <div className="text-xs text-muted-foreground mt-6">
        Zeitraum: letzte {data.windowHours}h · Stand{" "}
        {new Date(data.generatedAt).toLocaleTimeString()}
      </div>
    </>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
  tone?: "success" | "danger";
}) {
  const toneCls =
    tone === "success"
      ? "text-emerald-600"
      : tone === "danger"
        ? "text-destructive"
        : "text-foreground";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="size-4" />
        {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function BarList({ items }: { items: { label: string; value: number; sub?: string }[] }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.label}>
          <div className="flex justify-between text-sm mb-1">
            <span className="truncate font-medium">{i.label}</span>
            <span className="text-muted-foreground tabular-nums">{i.sub ?? i.value}</span>
          </div>
          <div className="h-2 rounded bg-muted overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
